import React from "react";
import { Paperclip } from "lucide-react";
import { TaskAttachment } from "../../../types";
import AttachmentList from "../../forms/AttachmentList";
import AttachmentUploader from "../../forms/AttachmentUploader";
import PendingAttachmentList from "../../forms/PendingAttachmentList";
import AttachmentViewer from "../AttachmentViewer";
import { useAttachmentViewer } from "../../../hooks/useAttachmentViewer";

interface EditAttachmentsSectionProps {
  mode: "edit";
  attachments: TaskAttachment[];
  onUpload: (file: File) => Promise<void>;
  onDelete: (attachmentId: string) => void;
  onRename?: (attachmentId: string, newName: string) => Promise<boolean>;
  isDeleting: string | null;
}

interface AddAttachmentsSectionProps {
  mode: "add";
  pendingFiles: File[];
  onUpload: (file: File) => Promise<void>;
  onRemovePendingFile: (index: number) => void;
}

export type TaskAttachmentsSectionProps =
  | EditAttachmentsSectionProps
  | AddAttachmentsSectionProps;

/**
 * The Attachments section of the task dialog. Edit mode uploads straight to
 * storage and lists persisted TaskAttachment rows, with an in-app viewer
 * (arrow keys page through the task's other attachments) opened via its own
 * local state. Add mode has no task id yet, so files are staged locally
 * (`pendingFiles`) and only uploaded once the task is created — see
 * useCreateTask's `pending_files` handling; there's nothing to preview yet,
 * so no viewer there.
 */
const TaskAttachmentsSection: React.FC<TaskAttachmentsSectionProps> = (
  props,
) => {
  const { mode, onUpload } = props;
  const count =
    mode === "edit" ? props.attachments.length : props.pendingFiles.length;
  const { viewerIndex, openViewer, closeViewer } = useAttachmentViewer();

  return (
    <div>
      <label className="block text-sm font-medium text-warm-text-secondary mb-2 flex items-center gap-2">
        <Paperclip size={16} />
        Attachments
        {count > 0 && (
          <span className="text-xs text-warm-text-tertiary font-normal">
            ({count})
          </span>
        )}
      </label>

      <div className="space-y-2">
        <AttachmentUploader currentCount={count} onUpload={onUpload} />
        {mode === "add" && (
          <p className="text-xs text-warm-text-tertiary">
            Files upload when you save the task.
          </p>
        )}
        {mode === "edit" ? (
          <AttachmentList
            attachments={props.attachments}
            onDelete={props.onDelete}
            onRename={props.onRename}
            onOpen={openViewer}
            openIndex={viewerIndex}
            isDeleting={props.isDeleting}
          />
        ) : (
          <PendingAttachmentList
            files={props.pendingFiles}
            onRemove={props.onRemovePendingFile}
          />
        )}
      </div>

      {mode === "edit" && viewerIndex !== null && (
        <AttachmentViewer
          key={viewerIndex}
          attachments={props.attachments}
          initialIndex={viewerIndex}
          onClose={closeViewer}
        />
      )}
    </div>
  );
};

export default TaskAttachmentsSection;
